// src/utils/wallSnapUtils.js
// Pure geometry utilities for wall-snap behaviour (no THREE.js dependency).

/**
 * Signed distance from point (px,py) to infinite line through (x1,y1)-(x2,y2).
 * Positive = left side of the direction vector, negative = right side.
 * Also returns the closest point on the *segment* and the outward normal.
 *
 * @returns {{ dist: number, projX: number, projY: number, nx: number, ny: number, t: number }}
 */
export function pointToLineDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) {
    // Degenerate segment
    const d = Math.hypot(px - x1, py - y1);
    return { dist: d, projX: x1, projY: y1, nx: 0, ny: 0, t: 0 };
  }

  // Parameter along segment [0,1]
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const projX = x1 + t * dx;
  const projY = y1 + t * dy;

  // Signed distance (cross product gives sign)
  const len = Math.sqrt(lenSq);
  const cross = dx * (py - y1) - dy * (px - x1); // positive = left
  const signedDist = cross / len;

  // Normal pointing towards the point (inward toward room center)
  const absDist = Math.abs(signedDist);
  let nx = 0;
  let ny = 0;
  if (absDist > 1e-12) {
    nx = (px - projX) / absDist;
    ny = (py - projY) / absDist;
  }

  return { dist: signedDist, projX, projY, nx, ny, t };
}

/**
 * Find the nearest wall segment and compute snap position + angle.
 *
 * @param {number} cx - Panel center X (tentative)
 * @param {number} cy - Panel center Y (tentative)
 * @param {Array<{x1,y1,x2,y2}>} segments - Wall segments in world coords
 * @param {number} snapThreshold - Max distance (world) to activate snap
 * @param {number} halfDepth - Half of panel depth in world units
 * @param {number} clearance - Wall clearance in world units
 * @returns {null|{ snapX: number, snapY: number, rotation: number, wallDx: number, wallDy: number, seg: object }}
 */
export function findSnapWall(cx, cy, segments, snapThreshold, halfDepth, clearance) {
  let best = null;
  let bestAbsDist = Infinity;

  for (const seg of segments) {
    const info = pointToLineDistance(cx, cy, seg.x1, seg.y1, seg.x2, seg.y2);
    const absDist = Math.abs(info.dist);

    // Only consider if panel center is within snap threshold
    if (absDist > snapThreshold || absDist < 1e-6) continue;

    // Only consider if the projection lands on the segment (not just endpoint vicinity)
    if (info.t <= 0.001 || info.t >= 0.999) continue;

    if (absDist < bestAbsDist) {
      bestAbsDist = absDist;

      const dx = seg.x2 - seg.x1;
      const dy = seg.y2 - seg.y1;
      const len = Math.hypot(dx, dy);
      const wallDx = dx / len;
      const wallDy = dy / len;

      // Wall angle: atan2 of the wall direction vector
      // Panel's local X axis aligns with the wall
      let rotation = Math.atan2(dy, dx);

      // The panel's local +Y (dimension/away-from-wall side) must point
      // in the same direction as the normal (toward room interior).
      // Local +Y from rotation = (-sin(rotation), cos(rotation))
      const localYx = -Math.sin(rotation);
      const localYy = Math.cos(rotation);
      // If dot(localY, normal) < 0, dimensions face the wall → flip
      if (localYx * info.nx + localYy * info.ny < 0) {
        rotation += Math.PI;
      }

      // Snap position: push panel center to (halfDepth + clearance) away from wall
      const offset = halfDepth + clearance;
      const snapX = info.projX + info.nx * offset;
      const snapY = info.projY + info.ny * offset;

      best = { snapX, snapY, rotation, wallDx, wallDy, seg, nx: info.nx, ny: info.ny };
    }
  }

  return best;
}

/**
 * Project mouse delta onto wall direction (sliding along wall).
 *
 * @returns {{ dx: number, dy: number }}
 */
export function slideAlongWall(deltaX, deltaY, wallDx, wallDy) {
  const dot = deltaX * wallDx + deltaY * wallDy;
  return { dx: dot * wallDx, dy: dot * wallDy };
}

/**
 * Clamp the panel center so that the panel (rotated) stays within the segment bounds.
 * The panel's local X axis is aligned with wall direction.
 *
 * @param {number} x - Panel center X
 * @param {number} y - Panel center Y
 * @param {object} seg - { x1, y1, x2, y2 }
 * @param {number} wallDx - Unit wall direction X
 * @param {number} wallDy - Unit wall direction Y
 * @param {number} halfWidth - Half of panel width in world units
 * @returns {{ x: number, y: number }}
 */
export function clampSlideToSegment(x, y, seg, wallDx, wallDy, halfWidth) {
  // Project segment endpoints onto wall direction
  const s1 = seg.x1 * wallDx + seg.y1 * wallDy;
  const s2 = seg.x2 * wallDx + seg.y2 * wallDy;
  const sMin = Math.min(s1, s2);
  const sMax = Math.max(s1, s2);

  // Project panel center onto wall direction
  let proj = x * wallDx + y * wallDy;

  // Clamp so panel edges stay within segment
  proj = Math.max(sMin + halfWidth, Math.min(sMax - halfWidth, proj));

  // Reconstruct the clamped center along the wall direction, keep perpendicular component
  const origProj = x * wallDx + y * wallDy;
  const diff = proj - origProj;

  return { x: x + diff * wallDx, y: y + diff * wallDy };
}

// ─── Segment-segment intersection ────────────────────

function segmentsIntersect(ax1, ay1, ax2, ay2, bx1, by1, bx2, by2) {
  const d1x = ax2 - ax1, d1y = ay2 - ay1;
  const d2x = bx2 - bx1, d2y = by2 - by1;
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-12) return false; // parallel
  const dx = bx1 - ax1, dy = by1 - ay1;
  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;
  // Use small epsilon to ignore touching-at-endpoint as intersection
  return t > 0.001 && t < 0.999 && u > 0.001 && u < 0.999;
}

/**
 * Get the 4 world-space corners of a rotated panel.
 */
export function getPanelCorners(panel, ds) {
  const halfW = (panel.totalWidthCm / ds) / 2;
  const halfD = (panel.depthCm / ds) / 2;
  const rot = panel.rotation || 0;
  const cos = Math.cos(rot), sin = Math.sin(rot);
  const locals = [[-halfW, -halfD], [halfW, -halfD], [halfW, halfD], [-halfW, halfD]];
  return locals.map(([lx, ly]) => ({
    x: panel.x + lx * cos - ly * sin,
    y: panel.y + lx * sin + ly * cos,
  }));
}

/**
 * Check if a rotated panel intersects any wall segment.
 * Returns true if any wall segment crosses any panel edge.
 */
export function panelIntersectsWalls(panel, segments, ds) {
  const corners = getPanelCorners(panel, ds);
  // 4 panel edges
  const edges = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i], b = corners[(i + 1) % 4];
    edges.push([a.x, a.y, b.x, b.y]);
  }
  for (const seg of segments) {
    for (const [ex1, ey1, ex2, ey2] of edges) {
      if (segmentsIntersect(seg.x1, seg.y1, seg.x2, seg.y2, ex1, ey1, ex2, ey2)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Hit-test a point against a rotated panel (inverse-rotate to local AABB test).
 *
 * @param {number} worldX - Click X in world
 * @param {number} worldY - Click Y in world
 * @param {object} panel - { x, y, totalWidthCm, depthCm, rotation }
 * @param {number} ds - Drawing scale
 * @returns {boolean}
 */
export function hitTestRotatedPanel(worldX, worldY, panel, ds) {
  const halfW = (panel.totalWidthCm / ds) / 2;
  const halfD = (panel.depthCm / ds) / 2;
  const rot = panel.rotation || 0;

  // Translate to panel center
  const dx = worldX - panel.x;
  const dy = worldY - panel.y;

  // Inverse-rotate
  const cosR = Math.cos(-rot);
  const sinR = Math.sin(-rot);
  const lx = dx * cosR - dy * sinR;
  const ly = dx * sinR + dy * cosR;

  return lx >= -halfW && lx <= halfW && ly >= -halfD && ly <= halfD;
}

// ─── Corner extraction & snap for dimension tool ─────

/**
 * Extract unique wall corner points from clipped segments.
 * Merges points closer than mergeThreshold into one.
 *
 * @param {Array<{x1,y1,x2,y2}>} clippedSegments
 * @param {number} mergeThreshold - Distance below which points are merged
 * @returns {Array<{x: number, y: number}>}
 */
export function extractWallCorners(clippedSegments, mergeThreshold = 0.5) {
  const raw = [];
  for (const seg of clippedSegments) {
    raw.push({ x: seg.x1, y: seg.y1 });
    raw.push({ x: seg.x2, y: seg.y2 });
  }

  const corners = [];
  for (const pt of raw) {
    let merged = false;
    for (const c of corners) {
      if (Math.hypot(pt.x - c.x, pt.y - c.y) < mergeThreshold) {
        // Average to refine position
        c.x = (c.x + pt.x) / 2;
        c.y = (c.y + pt.y) / 2;
        merged = true;
        break;
      }
    }
    if (!merged) corners.push({ x: pt.x, y: pt.y });
  }
  return corners;
}

/**
 * Find the nearest corner to a given world point.
 * Returns the corner if within snapThreshold, otherwise the original point.
 *
 * @param {number} worldX
 * @param {number} worldY
 * @param {Array<{x: number, y: number}>} corners
 * @param {number} snapThreshold
 * @returns {{x: number, y: number, snapped: boolean}}
 */
export function findNearestCorner(worldX, worldY, corners, snapThreshold) {
  let bestDist = Infinity;
  let bestCorner = null;
  for (const c of corners) {
    const d = Math.hypot(worldX - c.x, worldY - c.y);
    if (d < bestDist) {
      bestDist = d;
      bestCorner = c;
    }
  }
  if (bestCorner && bestDist < snapThreshold) {
    return { x: bestCorner.x, y: bestCorner.y, snapped: true };
  }
  return { x: worldX, y: worldY, snapped: false };
}

/**
 * Priority-based dimension snap: endpoint > midpoint > panelCorner > nearest > free.
 *
 * @param {number} worldX
 * @param {number} worldY
 * @param {Array<{x1,y1,x2,y2}>} clippedSegments
 * @param {Array} panels - Panel objects with { x, y, totalWidthCm, depthCm, rotation }
 * @param {number} ds - Drawing scale
 * @param {number} snapThreshold - Max distance in world units
 * @returns {{x: number, y: number, type: 'endpoint'|'midpoint'|'panelCorner'|'nearest'|'free', snapped: boolean}}
 */
export function findBestDimSnap(worldX, worldY, clippedSegments, panels, ds, snapThreshold) {
  // Priority 1: Wall endpoints (corners)
  const corners = extractWallCorners(clippedSegments, 0.5);
  let bestDist = Infinity;
  let bestPt = null;

  for (const c of corners) {
    const d = Math.hypot(worldX - c.x, worldY - c.y);
    if (d < bestDist) { bestDist = d; bestPt = c; }
  }
  if (bestPt && bestDist < snapThreshold) {
    return { x: bestPt.x, y: bestPt.y, type: 'endpoint', snapped: true };
  }

  // Priority 2: Wall segment midpoints
  bestDist = Infinity;
  bestPt = null;
  for (const seg of clippedSegments) {
    const mx = (seg.x1 + seg.x2) / 2;
    const my = (seg.y1 + seg.y2) / 2;
    const d = Math.hypot(worldX - mx, worldY - my);
    if (d < bestDist) { bestDist = d; bestPt = { x: mx, y: my }; }
  }
  if (bestPt && bestDist < snapThreshold) {
    return { x: bestPt.x, y: bestPt.y, type: 'midpoint', snapped: true };
  }

  // Priority 3: Panel corners
  bestDist = Infinity;
  bestPt = null;
  if (panels && panels.length > 0) {
    for (const panel of panels) {
      const pCorners = getPanelCorners(panel, ds);
      for (const pc of pCorners) {
        const d = Math.hypot(worldX - pc.x, worldY - pc.y);
        if (d < bestDist) { bestDist = d; bestPt = pc; }
      }
    }
  }
  if (bestPt && bestDist < snapThreshold) {
    return { x: bestPt.x, y: bestPt.y, type: 'panelCorner', snapped: true };
  }

  // Priority 4: Nearest point on wall (perpendicular projection, excluding endpoints)
  bestDist = Infinity;
  bestPt = null;
  for (const seg of clippedSegments) {
    const info = pointToLineDistance(worldX, worldY, seg.x1, seg.y1, seg.x2, seg.y2);
    // Exclude very near endpoints (t between 0.02 and 0.98)
    if (info.t < 0.02 || info.t > 0.98) continue;
    const d = Math.abs(info.dist);
    if (d < bestDist) { bestDist = d; bestPt = { x: info.projX, y: info.projY }; }
  }
  if (bestPt && bestDist < snapThreshold) {
    return { x: bestPt.x, y: bestPt.y, type: 'nearest', snapped: true };
  }

  // Fallback: free point
  return { x: worldX, y: worldY, type: 'free', snapped: false };
}

/**
 * Apply orthogonal constraint: snap the second point to be strictly
 * horizontal or vertical relative to the first point.
 * Picks whichever axis has the larger delta.
 */
export function applyOrtho(p1, p2) {
  const dx = Math.abs(p2.x - p1.x);
  const dy = Math.abs(p2.y - p1.y);
  if (dx >= dy) {
    return { x: p2.x, y: p1.y }; // horizontal
  }
  return { x: p1.x, y: p2.y };   // vertical
}

/**
 * Compute signed perpendicular distance from a point to the line P1→P2.
 * Positive = left side of the direction vector.
 */
export function computePerpOffset(p1, p2, mouse) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return 0;
  return (dx * (mouse.y - p1.y) - dy * (mouse.x - p1.x)) / len;
}

/**
 * Compute offset for a projected (axis-aligned) dimension.
 * For axis='h': offset = vertical distance from midpoint-Y of P1,P2 to mouse.
 * For axis='v': offset = horizontal distance from midpoint-X of P1,P2 to mouse.
 * Falls back to perpendicular offset if axis is not set.
 */
export function computeProjOffset(p1, p2, mouse, axis) {
  if (axis === 'h') {
    return mouse.y - (p1.y + p2.y) / 2;
  }
  if (axis === 'v') {
    return mouse.x - (p1.x + p2.x) / 2;
  }
  return computePerpOffset(p1, p2, mouse);
}
